import { useState } from 'react';

import { Box, Card, CardContent, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { getDashboardTrend } from '@/lib/api/dashboard';
import { VOICE_SCORE_LABELS } from '@/lib/api/voice-history';
import { PROGRAM_LABEL } from '@/lib/program-labels';

const SERIES = [
  { key: 'bgtAvgScore', label: `${PROGRAM_LABEL.bgt.short} 평균`, color: '#6A1B9A' },
  { key: 'hearing', label: VOICE_SCORE_LABELS.hearingScore, color: '#1976D2' },
  { key: 'cognitive', label: VOICE_SCORE_LABELS.cognitiveScore, color: '#2E7D32' },
  { key: 'stress', label: VOICE_SCORE_LABELS.stressScore, color: '#ED6C02' },
] as const;

const RANGES = [
  { value: 14, label: '2주' },
  { value: 28, label: '4주' },
  { value: 90, label: '3개월' },
];

/**
 * 시설 단위 점수 추이.
 *
 * 개인이 아니라 시설 전체가 나아지는지를 보는 카드다. 그날 데이터가 있는 사람만
 * 평균에 넣으므로, 점수 하락과 사용 인원 감소가 섞이지 않는다(사용 인원은 툴팁에 별도 표기).
 */
export function FacilityTrendCard() {
  const [days, setDays] = useState(28);

  const query = useQuery({
    queryKey: ['dashboard', 'trend', days],
    queryFn: () => getDashboardTrend(days),
    staleTime: 5 * 60 * 1000,
  });

  const points = query.data?.points ?? [];
  const hasAny = points.some(
    (p) => p.bgtAvgScore !== null || p.hearing !== null || p.cognitive !== null,
  );

  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            시설 점수 추이
          </Typography>
          <Box sx={{ flex: 1 }} />
          <TextField
            select
            size="small"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            sx={{ width: 100 }}
            aria-label="조회 기간"
          >
            {RANGES.map((r) => (
              <MenuItem key={r.value} value={r.value}>
                {r.label}
              </MenuItem>
            ))}
          </TextField>
        </Stack>

        {query.isError && (
          <Typography variant="body2" color="error.main">
            추이를 불러오지 못했습니다.
          </Typography>
        )}

        {!query.isLoading && !query.isError && !hasAny && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            표시할 집계 데이터가 없습니다.
          </Typography>
        )}

        {hasAny && (
          <Box sx={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={points} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11 }}
                  tickFormatter={(d: string) => d.slice(5)}
                  minTickGap={24}
                />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                <Tooltip
                  formatter={(value, name) => [value ?? '—', name]}
                  // 점수와 사용 인원을 함께 보여줘야 "점수 하락"과 "사용자 감소"를 구분할 수 있다.
                  labelFormatter={(label) => {
                    const p = points.find((x) => x.date === label);
                    return p
                      ? `${p.date} · ${PROGRAM_LABEL.bgt.short} ${p.bgtUsers}명 · ${PROGRAM_LABEL.voice.short} ${p.voiceUsers}명`
                      : String(label);
                  }}
                />
                <Legend />
                {SERIES.map((s) => (
                  <Line
                    key={s.key}
                    type="monotone"
                    dataKey={s.key}
                    name={s.label}
                    stroke={s.color}
                    strokeWidth={2}
                    dot={false}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </Box>
        )}
        <Typography variant="caption" color="text.disabled">
          그날 사용한 입소자만 평균에 포함 · 마음온도는 높을수록 평온
        </Typography>
      </CardContent>
    </Card>
  );
}
